import { useCallback, useRef } from 'react';
import { Stack, Text, ThemeIcon } from '@mantine/core';
import { IconHeadphones } from '@tabler/icons-react';
import { useBindMediaElement, type PlaybackController } from './playbackController';

export interface PlaybackHandoff {
  /** Position (seconds) to continue from. */
  time: number;
  /** Whether playback was running when the viewer switched. */
  play: boolean;
}

/**
 * Listen mode for a video session: plays the audio-only MP3 with the
 * session artwork and title. Takes over the position and play state from the
 * video (and hands them back), reports progress through the same callbacks so
 * telemetry and resume carry on, and asks to switch back to the video if the
 * audio cannot play.
 */
export function ListenPlayer({
  audioUrl,
  artworkUrl,
  title,
  color,
  handoff,
  resumeAt,
  controller,
  onProgress,
  onCompleted,
  onFailed,
}: {
  audioUrl: string;
  artworkUrl?: string | null;
  title?: string | null;
  color?: string | null;
  handoff?: PlaybackHandoff | null;
  resumeAt: number;
  controller?: PlaybackController | null;
  onProgress: (sec: number, pct: number) => void;
  onCompleted: (sec: number, pct: number) => void;
  onFailed: () => void;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const seekedRef = useRef(false);
  const bind = useBindMediaElement(controller);
  const setRef = useCallback(
    (node: HTMLAudioElement | null) => {
      ref.current = node;
      bind(node);
    },
    [bind],
  );

  const onLoadedMetadata = () => {
    const el = ref.current;
    if (!el || seekedRef.current) return;
    seekedRef.current = true;
    const target = handoff ? handoff.time : resumeAt;
    if (target > 1 && (!(el.duration > 0) || target < el.duration - 2)) {
      try {
        el.currentTime = target;
      } catch {
        /* ignore seek errors */
      }
    }
    if (handoff?.play) el.play().catch(() => { /* autoplay blocked: stays paused */ });
  };

  const handleTime = () => {
    const el = ref.current;
    if (!el) return;
    const pct = el.duration > 0 ? Math.min(100, (el.currentTime / el.duration) * 100) : 0;
    onProgress(Math.floor(el.currentTime), Math.round(pct * 100) / 100);
  };

  const handleEnd = () => {
    const el = ref.current;
    if (!el) return;
    onCompleted(Math.floor(el.duration || 0), el.duration > 0 ? 100 : 0);
  };

  return (
    <Stack
      p="xl"
      gap="md"
      align="center"
      style={{
        background: color
          ? `linear-gradient(135deg, ${color}33, var(--mantine-color-brand-light))`
          : 'linear-gradient(135deg, var(--mantine-color-brand-light), var(--mantine-color-default-hover))',
        flex: 1,
        minHeight: 0,
        justifyContent: 'center',
        width: '100%',
      }}
    >
      {artworkUrl ? (
        <img
          src={artworkUrl}
          alt=""
          style={{
            maxWidth: 'min(40vh, 360px)',
            maxHeight: 'min(28vh, 220px)',
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            borderRadius: 16,
            boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
          }}
        />
      ) : (
        <ThemeIcon size={120} radius={120} variant="filled" color="brand">
          <IconHeadphones size={56} />
        </ThemeIcon>
      )}
      {title && (
        <Text fw={600} ta="center" lineClamp={2} style={{ maxWidth: 640 }}>
          {title}
        </Text>
      )}
      <audio
        ref={setRef}
        src={audioUrl}
        controls
        preload="metadata"
        onLoadedMetadata={onLoadedMetadata}
        onTimeUpdate={handleTime}
        onEnded={handleEnd}
        onError={onFailed}
        style={{ width: '100%', maxWidth: 640 }}
      />
    </Stack>
  );
}
